<template>
    <div class="qrcode-generator">
        <h3>在线二维码生成器</h3>

        <!-- 输入框 -->
        <div class="input-group">
            <input
                    v-model="text"
                    type="text"
                    placeholder="请输入要生成二维码的文本或链接"
                    @input="generateQrCodeDebounced"
            />
            <button @click="generateQrCode">生成二维码</button>
        </div>

        <!-- 二维码预览 -->
        <div v-if="qrCodeUrl" class="preview">
            <h4>二维码预览：</h4>
            <img :src="qrCodeUrl" alt="二维码" />
        </div>

        <!-- 下载按钮 -->
        <button v-if="qrCodeUrl" @click="downloadQrCode" class="download-btn">
            ⬇ 下载二维码图片
        </button>

        <!-- iOS 提示 -->
        <p v-if="qrCodeUrl" class="hint">
            📱 iOS 用户可长按图片保存
        </p>

        <!-- 内容说明区 -->
        <div class="tool-content">
            <h2>📱 免费二维码生成器</h2>
            <p>快速生成自定义二维码，支持文字、网址、邮箱、电话号码等多种内容类型。高分辨率输出，可直接下载用于印刷或线上分享。</p>

            <h3>✨ 功能特点</h3>
            <ul>
                <li><strong>内容广泛</strong>：支持 URL、文本、邮箱、电话、WiFi 等</li>
                <li><strong>高清输出</strong>：生成 500x500 像素高清二维码</li>
                <li><strong>即时生成</strong>：输入内容实时显示二维码</li>
                <li><strong>免费无限制</strong>：无次数限制，无需注册</li>
            </ul>

            <h3>📝 使用教程</h3>
            <ol>
                <li>在输入框输入要编码的内容</li>
                <li>二维码实时生成，可立即扫码测试</li>
                <li>点击「下载二维码」保存 PNG 图片</li>
            </ol>

            <h3>💡 内容格式示例</h3>
            <p><strong>网址</strong>：<code>https://example.com</code><br>
            <strong>邮箱</strong>：<code>mailto:hello@example.com</code><br>
            <strong>电话</strong>：<code>tel:+8613800138000</code><br>
            <strong>WiFi</strong>：<code>WIFI:T:WPA;S:网络名;P:密码;;</code></p>

            <h3>📐 印刷建议</h3>
            <p>下载的二维码为 500x500 像素，印刷时建议最小尺寸 2x2cm，确保扫码识别率。复杂内容（长网址）建议使用短链接再生成二维码。</p>

            <h3>🔒 隐私说明</h3>
            <p>使用 qrcode.js 在浏览器本地生成，内容不会上传到服务器。</p>
        </div>
    </div>
</template>

<script>
    import seoMixin from '../js/seo-mixin';
import QRCode from 'qrcode';

    export default {
        name: 'QrCodeGenerator',
        mixins: [seoMixin],
        data() {
            return {
                seoTitle: '免费二维码生成器在线工具 - FreeToolBox',
                seoDescription: '免费在线二维码生成器，快速生成自定义二维码。支持文字、链接、邮箱等内容，高分辨率输出，可直接下载为PNG图片。',
                seoKeywords: '二维码生成,在线二维码,qr码生成,二维码制作,免费二维码',
                text: '', // 初始为空
                qrCodeUrl: '',
                generateQrCodeDebounced: null,
            };
        },
        methods: {
            // 防抖函数
            debounce(func, delay) {
                let timer;
                return () => {
                    clearTimeout(timer);
                    timer = setTimeout(() => func.call(this), delay);
                };
            },

            async generateQrCode() {
                const trimmed = this.text.trim();
                if (!trimmed) {
                    this.qrCodeUrl = '';
                    return;
                }

                try {
                    this.qrCodeUrl = await QRCode.toDataURL(trimmed, {
                        width: 400,      // 更高分辨率
                        margin: 2,
                        errorCorrectionLevel: 'H', // 容错率更高（可抗遮挡）
                        color: {
                            dark: '#000000',
                            light: '#ffffff',
                        },
                    });
                } catch (err) {
                    console.error('生成二维码失败:', err);
                    this.qrCodeUrl = '';
                }
            },

            downloadQrCode() {
                if (!this.qrCodeUrl) return;

                const a = document.createElement('a');
                a.href = this.qrCodeUrl;
                a.download = `qrcode-${Date.now()}.png`; // 避免重名
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                a.focus(); // 增强兼容性
            },
        },
        mounted() {
            // 初始化防抖
            this.generateQrCodeDebounced = this.debounce(this.generateQrCode, 300);

            // 生成默认内容（可选）
            this.text = 'http://freetoolbox.top';
            this.generateQrCode();
        },
    };
</script>

<style scoped>
    .qrcode-generator {
        padding: 20px;
        border: 1px solid #ccc;
        border-radius: 8px;
        max-width: 600px;
        margin: 40px auto;
        font-family: Arial, sans-serif;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }

    h3 {
        text-align: center;
        color: #333;
        margin-bottom: 20px;
    }

    .input-group {
        display: flex;
        gap: 10px;
        margin-bottom: 20px;
    }

    .input-group input {
        flex: 1;
        padding: 10px;
        font-size: 14px;
        border: 1px solid #ddd;
        border-radius: 4px;
        outline: none;
    }

    .input-group input:focus {
        border-color: #409eff;
    }

    .input-group button {
        padding: 10px 15px;
        font-size: 14px;
        background-color: #409eff;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.3s;
    }

    .input-group button:hover {
        background-color: #357ae8;
    }

    .preview {
        text-align: center;
        margin: 20px 0;
    }

    .preview img {
        width: 200px;
        height: 200px;
        border: 1px solid #eee;
        border-radius: 8px;
        box-shadow: 0 1px 5px rgba(0, 0, 0, 0.1);
        object-fit: contain;
        background: white;
        padding: 8px;
    }

    .download-btn {
        display: block;
        margin: 20px auto;
        padding: 10px 20px;
        font-size: 16px;
        background-color: #28a745;
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        transition: background-color 0.3s;
    }

    .download-btn:hover {
        background-color: #218838;
    }

    .hint {
        text-align: center;
        color: #888;
        font-size: 0.9em;
        margin-top: 10px;
    }
    /* 内容说明区样式 */
    .tool-content {
        max-width: 800px;
        margin: 40px auto;
        padding: 30px;
        background: #f8f9fa;
        border-radius: 12px;
        text-align: left;
    }
    .tool-content h2 {
        color: #333;
        font-size: 1.5em;
        margin-bottom: 15px;
        border-bottom: 2px solid #000;
        padding-bottom: 10px;
    }
    .tool-content h3 {
        color: #555;
        font-size: 1.2em;
        margin: 20px 0 10px;
    }
    .tool-content p, .tool-content li {
        color: #666;
        line-height: 1.8;
        margin-bottom: 10px;
    }
    .tool-content ul, .tool-content ol {
        padding-left: 20px;
        margin-bottom: 15px;
    }
    .tool-content code {
        background: #e9ecef;
        padding: 2px 6px;
        border-radius: 3px;
        font-family: monospace;
        color: #d63384;
    }
</style>